import React from 'react'
import { Link } from 'react-router-dom'
import { Linkedin, Facebook, Mail, Instagram } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { trackSocialClick } from '../utils/metaPixel'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const { t } = useTranslation()

  const footerLinks = {
    services: [
      { name: t('footer.leadGeneration', 'Lead Generation'), href: '#services' },
      { name: t('footer.crmAutomation', 'CRM Automation'), href: '#services' },
      { name: t('footer.ecommerce', 'E-commerce'), href: '#services' },
      { name: t('footer.aiWorkflows', 'AI Workflows'), href: '#services' },
      { name: t('footer.customIntegrations', 'Custom Integrations'), href: '#services' },
    ],
    company: [
      { name: t('footer.aboutUs', 'About Us'), href: '#about' },
      { name: t('footer.process', 'Process'), href: '#process' },
      { name: t('footer.pricing', 'Pricing'), href: '#pricing' },
      { name: t('footer.faq', 'FAQ'), href: '#faq' },
      { name: t('footer.contact', 'Contact'), href: '#cta' },
    ],
    resources: [
      { name: t('footer.n8nTemplates', 'n8n Templates'), href: 'https://n8n.io/workflows' },
      { name: t('footer.documentation', 'Documentation'), href: 'https://docs.n8n.io/' }
    ]
  }

  const socialLinks = [
    { icon: <Linkedin className="w-5 h-5" />, href: 'https://www.linkedin.com/company/nodatx', label: t('footer.linkedin', 'LinkedIn') },
    { icon: <Instagram className="w-5 h-5" />, href: 'https://www.instagram.com/nodatx.c0m', label: t('footer.instagram', 'Instagram') },
    { icon: <Facebook className="w-5 h-5" />, href: 'https://www.facebook.com/profile.php?id=61583677731046', label: t('footer.facebook', 'Facebook') },
  ]

  return (
    <footer className="bg-[#0a0a0f] border-t border-white/10">
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-6">
              <div className="w-12 h-12 bg-transparent flex items-center justify-center">
                <img src="/nodatx.png" alt={t('footer.logoAlt', 'Nodatx Logo')} className="w-full h-full object-contain" />
              </div>
              <span className="text-xl font-bold text-white">
                Noda<span className="text-purple-400">tx</span>
              </span>
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              {t('footer.brandDesc', "We build n8n workflows that eliminate repetitive tasks and give you back 20+ hours every week. Automation experts for growing businesses.")}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-purple-400 hover:border-purple-400/50 transition-all"
                  aria-label={social.label}
                  onClick={() => trackSocialClick(social.label)}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.services', 'Services')}</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-gray-400 hover:text-purple-400 transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.company', 'Company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => {
                const href = link.href.startsWith('#') ? link.href : link.href
                return (
                  <li key={index}>
                    <Link to={href} className="text-gray-400 hover:text-purple-400 transition-colors">
                      {link.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.resources', 'Resources')}</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    className="text-gray-400 hover:text-purple-400 transition-colors"
                    target={link.href.startsWith('http') ? '_blank' : undefined}
                    rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            &copy; {currentYear} Nodatx. {t('footer.copyright', 'All rights reserved.')}
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy" className="text-gray-500 hover:text-white transition-colors">{t('footer.privacyPolicy', 'Privacy Policy')}</Link>
            <Link to="/terms" className="text-gray-500 hover:text-white transition-colors">{t('footer.termsOfService', 'Terms of Service')}</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer